"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { ChatInput } from "@/components/chat/ChatInput"
import { ChatMessages } from "@/components/chat/ChatMessages"
import { ToolCallDisplay } from "@/components/chat/ToolCallDisplay"
import { AgentCoreClient } from "@/lib/agentcore-client"
import type { AgentPattern } from "@/lib/agentcore-client"
import { loadAwsExports } from "@/lib/awsExports"
import { useBranding } from "@/lib/branding"
import { submitFeedback } from "@/services/feedbackService"
import { useAgentChat } from "@/hooks/useAgentChat"
import { useAuth } from "@/hooks/useAuth"
import { useDefaultTool } from "@/hooks/useToolRenderer"
import { buildContextPreamble, resolveEmbedSessionId, validatePlayerId } from "./context"
import type { TicketContext } from "./host/types"

interface PanelChatProps {
  /** Context from the host: a desk adapter's handshake, or the support workspace's player bar. */
  ticket: TicketContext
  /**
   * Overrides `ticket.ticketId` as the conversation-identity key. The support
   * workspace keys conversations per player (`player:<id>`) because it has no
   * ticket; the embedded panel omits this and stays keyed per ticket.
   */
  conversationKey?: string
  /**
   * Empty-state line shown when no validated player is in scope. The default
   * says "this ticket", which is right inside a support desk but wrong for
   * the workspace, which has no ticket to point at.
   */
  noPlayerHint?: string
}

/**
 * Panel-sized recomposition of the standalone chat: the same ChatMessages,
 * ChatInput, tool rendering, streaming loop (useAgentChat) and feedback path,
 * without the sidebar, routes or hero. Nothing here forks chat logic; it is
 * composition only.
 */
export function PanelChat({ ticket, conversationKey, noPlayerHint }: PanelChatProps) {
  const [input, setInput] = useState("")
  const [client, setClient] = useState<AgentCoreClient | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [questions, setQuestions] = useState<{ common: string[]; player: string[] }>({
    common: [],
    player: [],
  })
  const auth = useAuth()
  const branding = useBranding()

  // Conversation identity: per ticket in the embedded panel (reopening a
  // ticket resumes its conversation), per player in the support workspace.
  // No key means a fresh conversation per panel load.
  const sessionId = useMemo(
    () => resolveEmbedSessionId(conversationKey ?? ticket.ticketId, window.localStorage),
    [conversationKey, ticket.ticketId]
  )

  const { messages, setMessages, error, setError, sendMessage } = useAgentChat({
    client,
    sessionId,
    getAccessToken: () => auth.user?.access_token,
    setIsLoading,
  })

  // The context preamble rides on the first turn sent for a given
  // (session, player) pair in this panel load; a mid-ticket player change
  // (via onTicketChange upstream) re-arms it so the next turn re-scopes.
  const playerId = validatePlayerId(ticket.playerId)
  const contextSentForRef = useRef<string | null>(null)
  const contextKey = `${sessionId}:${playerId ?? ""}`

  // With a validated player in scope, the per-player investigation prompts
  // (the UC7-UC9 RTP/AML/safer-gambling questions from the profile) replace
  // the site-wide common questions: they say "this player" and the scoping
  // preamble binds them to the loaded id. Applies wherever PanelChat runs,
  // so a player-linked ticket in a future desk gets them too.
  const suggestions = playerId && questions.player.length > 0 ? questions.player : questions.common

  const messagesEndRef = useRef<HTMLDivElement>(null)

  useDefaultTool(({ name, args, status, result }) => (
    <ToolCallDisplay name={name} args={args} status={status} result={result} />
  ))

  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await loadAwsExports()
        if (!config.agentRuntimeArn) {
          throw new Error("Agent Runtime ARN not found in configuration")
        }
        setClient(
          new AgentCoreClient({
            runtimeArn: config.agentRuntimeArn,
            region: config.awsRegion || "us-east-1",
            pattern: (config.agentPattern || "strands-single-agent") as AgentPattern,
          })
        )
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(`Configuration error: ${errorMessage}`)
        console.error("Failed to load agent configuration:", err)
      }
    }

    async function loadProfile() {
      try {
        const response = await fetch("/profile-config.json")
        if (!response.ok) return
        const profile: { common_questions?: unknown; support_player_questions?: unknown } =
          await response.json()
        const asStrings = (value: unknown): string[] =>
          Array.isArray(value) ? value.filter((q): q is string => typeof q === "string") : []
        setQuestions({
          common: asStrings(profile.common_questions),
          player: asStrings(profile.support_player_questions),
        })
      } catch {
        // Optional; the panel works without suggestions.
      }
    }

    void loadConfig()
    void loadProfile()
    // Deliberately mount-only; config and profile do not change mid-load.
    // (No react-hooks/exhaustive-deps plugin is registered in this project's
    // ESLint config, so a disable directive here would itself error.)
  }, [])

  // Switching tickets in-place swaps the conversation entirely. Keyed on
  // sessionId alone on purpose; the setters are stable across renders.
  useEffect(() => {
    setMessages([])
    setError(null)
  }, [sessionId])

  useEffect(() => {
    // Same direct-scroll approach as ChatInterface: scroll the log container,
    // rAF-coalesced across streaming bursts.
    const container = messagesEndRef.current?.closest(".insight-scroll") as HTMLElement | null
    if (!container) return
    const frame = requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" })
    })
    return () => cancelAnimationFrame(frame)
  }, [messages])

  const send = (text: string): void => {
    if (!text.trim() || !client) return
    let wireMessage: string | undefined
    const arming = contextSentForRef.current !== contextKey
    if (arming) {
      const preamble = buildContextPreamble(ticket)
      // No usable player id → no preamble → a normal chat (required
      // degradation); mark the key handled either way so validation is not
      // retried every turn. Marked before the await so a second send cannot
      // double-inject the preamble mid-stream.
      if (preamble) wireMessage = `${preamble}${text}`
      contextSentForRef.current = contextKey
    }
    void sendMessage(text, wireMessage).then(delivered => {
      // A failed first turn never delivered the scoping preamble; re-arm so
      // the retry carries it instead of silently starting an unscoped chat.
      if (!delivered && arming && contextSentForRef.current === contextKey) {
        contextSentForRef.current = null
      }
    })
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!input.trim() || !client) return
    send(input)
    setInput("")
  }

  // Same feedback service, token and audit path as the standalone app.
  const handleFeedbackSubmit = async (
    messageContent: string,
    feedbackType: "positive" | "negative",
    comment: string
  ): Promise<void> => {
    try {
      const idToken = auth.user?.id_token
      if (!idToken) {
        throw new Error("Authentication required. Please log in again.")
      }
      await submitFeedback(
        { sessionId, message: messageContent, feedbackType, comment: comment || undefined },
        idToken
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error"
      console.error("Error submitting feedback:", err)
      setError(`Failed to submit feedback: ${errorMessage}`)
    }
  }

  // h-full, not h-screen: EmbedApp owns the viewport column and renders the
  // ticket-context header above this component, outside the auth gate.
  return (
    <div className="flex h-full w-full flex-col bg-background">
      {error && (
        <p className="m-2 flex-none rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
          {error}
        </p>
      )}

      <div className="min-h-0 grow overflow-clip">
        {messages.length === 0 ? (
          <div className="insight-scroll h-full overflow-y-auto p-3">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {playerId
                ? `Ask ${branding.productName} about this player: answers come from your organisation's data.`
                : (noPlayerHint ??
                  `Ask ${branding.productName} about this ticket: answers come from your organisation's data.`)}
            </p>
            {suggestions.length > 0 && (
              <div className="mt-3 flex flex-col gap-1.5">
                {suggestions.map(question => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => send(question)}
                    disabled={isLoading || !client}
                    className="rounded-md border border-border/70 bg-card/50 px-2.5 py-1.5 text-left text-xs leading-relaxed text-brand-ink-soft transition-colors duration-150 hover:border-brand-coral/60 hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-coral/50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <ChatMessages
            messages={messages}
            messagesEndRef={messagesEndRef}
            sessionId={sessionId}
            onFeedbackSubmit={handleFeedbackSubmit}
            // Compact rhythm for the ~360px panel; see ChatMessagesProps.
            className="space-y-4 p-3"
          />
        )}
      </div>

      <div className="flex-none border-t border-border/60">
        <ChatInput
          input={input}
          setInput={setInput}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          className="!p-2"
        />
      </div>
    </div>
  )
}
